import React, { useMemo, useState } from 'react';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { ButtonColor } from '../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import styles from './WarrantyRequestModal.module.scss';
import clsx from 'clsx';
import {
  BasketGetResponseDto,
  BasketPatchOrderDto,
  DeviceType,
  WarrantyRequestBasketClient,
  WarrantyRequestBasketQuery,
} from 'services/api/api-client.ts';
import { useWatch } from 'react-hook-form';
import { ArticleInfo } from './components/ArticleInfo.tsx';
import { SerialNumberInfo } from './components/SerialNumberInfo.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { Form } from './WarrantyRequestModal.tsx';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { getCurrentSupportedLanguage } from '../../../application/localization/localization.ts';
import { errorToString } from '../../../helpers/error-helpers.ts';

type OwnProps = {
  handleModalClose: () => void;
  serviceProductId: string;
  itemNumber: string;
  deviceName: string;
  articleName: string;
  isOpen: boolean;
  title: string;
  basketQuery: UseQueryResult<BasketGetResponseDto, unknown>;
};

export type Option = {
  key: string;
  text: string;
};

export const WarrantyRequestEditModal = (props: OwnProps) => {
  const {
    title,
    handleModalClose,
    isOpen,
    deviceName,
    articleName,
    itemNumber,
    basketQuery,
  } = props;
  const queryClient = useQueryClient();

  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isCloseAddingModalShown, setIsCloseAddingModalShown] = useState(false);
  const [isClearErrorsModalShown, setIsClearErrorsModalShown] = useState(false);

  const i18n = useScopedTranslation('Page.WarrantyRequest');
  const currentLanguage = getCurrentSupportedLanguage(i18n.i18n);

  const basketEdit = WarrantyRequestBasketClient.patchOrder;

  const editingDevice = WarrantyRequestBasketQuery.useGetCertainOrderQuery(
    {
      itemNumber: itemNumber,
      language: currentLanguage,
    },
    {
      staleTime: 60 * 1000,
    },
  );

  const receivedSelectedErrors: Option[] = editingDevice.data?.selectedErrors
    ? Object.entries(editingDevice.data?.selectedErrors).map(
        ([key, value]) => ({
          key,
          text: value,
        }),
      )
    : [];

  const clearErrors = () => {
    setIsClearErrorsModalShown(false);
    form.setValue('duringTherapy', false);
    form.setValue('notDuringTherapy', false);
    form.setValue('selectedErrors', []);
    form.setValue('errorDescription', '');
    form.clearErrors('areAnyErrorsSelected');
    form.clearErrors('isDurationSelected');
  };

  const showClearErrorsModal = () => {
    setIsClearErrorsModalShown(true);
  };

  const deviceType =
    editingDevice.data?.noFailureDuringTherapy ||
    editingDevice.data?.failureDuringTherapy
      ? DeviceType.Special
      : DeviceType.Default;

  const form = useAdvancedForm<Form>(
    async () => {
      try {
        setIsFormLoading(true);

        const dto: BasketPatchOrderDto = {
          serviceProduct: serviceProductId!,
          additionalErrorDescription: errorDescription,
          selectedErrors: selectedErrors.map(
            (selectedError) => selectedError.key,
          ),
          operatingHours: operatingHours ? Number(operatingHours) : null,
          resaleDate: dateOfResale,
          replacementPartSerialNumber: articleSerialNumber,
          replacementPartArticle: article,
          failureDuringTherapy: isDuringTherapy,
          noFailureDuringTherapy: isNotDuringTherapy,
        };

        await basketEdit(dto);
        await queryClient.refetchQueries({
          queryKey: WarrantyRequestBasketQuery.searchLastAddedDeviceQueryKey(
            props.serviceProductId,
          ),
        });

        handleModalClose();
        void basketQuery.refetch();
        await queryClient.invalidateQueries({
          queryKey: WarrantyRequestBasketQuery.getCertainOrderQueryKey(
            props.itemNumber,
          ),
        });
      } catch (e) {
        form.setValue('formSubmitErrorText', errorToString(e));
        setIsFormLoading(false);
      }
    },
    {
      defaultValues: {
        serviceProductId: editingDevice.data?.serviceProductId ?? '',
        article: editingDevice.data?.replacementPartArticle ?? '',
        articleSerialNumber:
          editingDevice.data?.replacementPartSerialNumber ?? '',
        selectedErrors: receivedSelectedErrors ?? [],
        operatingHours: editingDevice.data?.operatingHours ?? 0,
        dateOfResale: editingDevice.data?.resaleDate,
        duringTherapy: editingDevice.data?.failureDuringTherapy ?? false,
        notDuringTherapy: editingDevice.data?.noFailureDuringTherapy ?? false,
        errorDescription: editingDevice.data?.additionalErrorDescription ?? '',
      },
    },
  );

  const [
    serviceProductId,
    article,
    dateOfResale,
    selectedErrors,
    articleSerialNumber,
    errorDescription,
    operatingHours,
    isDuringTherapy,
    isNotDuringTherapy,
    formSubmitErrorText,
  ] = useWatch({
    control: form.control,
    name: [
      'serviceProductId',
      'article',
      'dateOfResale',
      'selectedErrors',
      'articleSerialNumber',
      'errorDescription',
      'operatingHours',
      'duringTherapy',
      'notDuringTherapy',
      'formSubmitErrorText',
    ],
  });

  const availableErrors = useMemo(() => {
    const result: Option[] = [];

    for (const key in editingDevice.data?.availableErrors) {
      result.push({
        key,
        text: editingDevice.data?.availableErrors[key],
      });
    }

    return result;
  }, [editingDevice.data?.availableErrors]);

  const removeSelectedError = (error: Option) => {
    const newSelectedErrors = selectedErrors.filter(
      (curErr) => curErr.key !== error.key,
    );

    form.setValue('selectedErrors', newSelectedErrors, { shouldDirty: true });
  };

  const handleSubmit = async () => {
    form.setValue('formSubmitErrorText', null);

    if (!selectedErrors.length) {
      form.setError('areAnyErrorsSelected', {
        message: i18n.t('required_error'),
      });
    } else {
      const result = await form.trigger();

      if (result) {
        void form.handleSubmitDefault();
      }
    }
  };

  const onModalClose = () => {
    if (Object.keys(form.formState.dirtyFields).length > 0) {
      setIsCloseAddingModalShown(true);
    } else {
      handleModalClose();
    }
  };

  return (
    <CustomModal
      containerClassName={styles.container}
      contentClassName={styles.overflowYScroll}
      isOpen={isOpen}
      titleText={title}
      cancelButtonText={i18n.t('cancel')}
      submitButtonText={i18n.t('edit_part')}
      onSubmit={handleSubmit}
      onCancel={onModalClose}
      submitButtonType={ButtonColor.Primary}
      isLoading={isFormLoading}
      isDisabled={isFormLoading}
      doNotCloseOnOutsideClick={false}
    >
      <div>
        <div className={clsx(styles.subtitle, styles.marginBottom10)}>
          {i18n.t('device')}
        </div>
        <Loading
          loading={editingDevice.isLoading}
          className={styles.absoluteContainer}
          wrapperClassName={styles.loader}
        >
          {editingDevice.data && (
            <div>
              <SerialNumberInfo
                serialNumber={editingDevice.data.serviceProductName}
                form={form}
                device={{
                  serialNumber: editingDevice.data.serviceProductName ?? '',
                  availableErrors: editingDevice.data.availableErrors,
                  deviceName: deviceName,
                  manufactureYear: '',
                }}
                dateOfResale={dateOfResale}
              />
              <div className={clsx(styles.subtitle, styles.marginBottom13)}>
                {i18n.t('replacement_part')}
              </div>
              <ArticleInfo
                deviceType={deviceType}
                isLoading={editingDevice.isLoading}
                article={editingDevice.data?.replacementPartArticle ?? ''}
                replacementPart={articleName}
                replacementPartNumber={
                  editingDevice.data?.replacementPartSerialNumber ?? ''
                }
                form={form}
                availableErrors={availableErrors}
                selectedErrors={selectedErrors}
                removeSelectedError={removeSelectedError}
                showClearErrorsModal={showClearErrorsModal}
                errorDescription={errorDescription}
                isEditingMode={true}
              />
            </div>
          )}
        </Loading>
      </div>
      <div
        className={clsx(
          formSubmitErrorText && styles.rowContainer,
          styles.buttonContainer,
          styles.justifyContentFlexEnd,
        )}
      >
        {formSubmitErrorText && (
          <div className={clsx(styles.unexpectedServerError, styles.flex1)}>
            {formSubmitErrorText}
          </div>
        )}
      </div>
      <CustomModal
        isOpen={isCloseAddingModalShown}
        bodyText={i18n.t('leave_add_modal_text')}
        onSubmit={() => {
          setIsCloseAddingModalShown(false);
          void handleSubmit();
        }}
        onClose={() => setIsCloseAddingModalShown(false)}
        onCancel={() => {
          setIsCloseAddingModalShown(false);
          handleModalClose();
        }}
        cancelButtonText={i18n.t('discard_changes')}
        submitButtonText={i18n.t('save_changes')}
        titleText={i18n.t('leave_edit_modal_title')}
        containerClassName={styles.modalBodyMinWidth480}
        submitButtonType={ButtonColor.Primary}
        cancelButtonType={ButtonColor.Warning}
      />
      <CustomModal
        isOpen={isClearErrorsModalShown}
        bodyText={i18n.t('clear_error')}
        onSubmit={clearErrors}
        onCancel={() => setIsClearErrorsModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('clear')}
        titleText={i18n.t('clear')}
        containerClassName={styles.modalBodyMinWidth480}
      />
    </CustomModal>
  );
};
